import React, { useEffect, useState } from 'react';

import SheetFrame from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/SheetFrame';
import PreferencesSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/PreferencesSection';
import GroupSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/GroupSection';
import LocationSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/LocationSection';
import MultiGroupSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/MultiGroupSection';
import MultiLocationSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/MultiLocationSection';
import TimeFrequencySection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/TimeFrequencySection';
import VariableSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/VariableSection';
import ForecastDateSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/ForecastDateSection';
import RoundsSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/RoundsSection';
import useDetailedViewMobile from 'hooks/Meteorology/Agro/TrackingForecast/DetailedView/Mobile/useDetailedViewMobile';

import PreferenceRow from './PreferenceRow';

type SheetSection =
  | 'preferences' | 'group' | 'location' | 'multigroup' | 'multilocation'
  | 'timefrequency' | 'variable' | 'forecastdate' | 'rounds' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('variable');
  const [groupSearch, setGroupSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [multiGroupSearch, setMultiGroupSearch] = useState('');
  const [multiLocationSearch, setMultiLocationSearch] = useState('');

  const {
    subpage,
    triggerRequest,
    clearSelection,
    savePreference,
    selectPreference,
    preferences,
    preferenceDeleteTrigger,
    groupedLocations,
    timeFrequency,
    variables,
    forecastDate,
    rounds,
  } = useDetailedViewMobile();

  useEffect(() => {
    if (open) setOpenSection('variable');
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  // Ordem das seções replica exatamente o painel desktop por subpage
  // (Agro/ControlPanel/TrackingForecast/DetailedView/index.tsx).
  const timeFrequencySection = (
    <TimeFrequencySection
      timeFrequency={timeFrequency}
      open={openSection === 'timefrequency'}
      onToggle={toggleSection('timefrequency')}
    />
  );

  const variableSection = (
    <VariableSection
      variables={variables}
      open={openSection === 'variable'}
      onToggle={toggleSection('variable')}
    />
  );

  return (
    <SheetFrame
      open={open}
      dismiss={dismiss}
      className="agro-tf-dv-control-panel-sheet"
      savePreference={savePreference}
      triggerRequest={triggerRequest}
      clearSelection={clearSelection}
    >
      <PreferencesSection
        preferences={preferences}
        renderRow={(preference) => (
          <PreferenceRow
            preference={preference}
            onSelect={() => { selectPreference(preference)(); dismiss(); }}
            onDelete={preferenceDeleteTrigger(preference.name)}
          />
        )}
        open={openSection === 'preferences'}
        onToggle={toggleSection('preferences')}
      />

      {subpage === 'locations' && (
        <>
          <MultiGroupSection
            groupedLocations={groupedLocations}
            search={multiGroupSearch}
            onSearchChange={setMultiGroupSearch}
            open={openSection === 'multigroup'}
            onToggle={toggleSection('multigroup')}
          />
          <MultiLocationSection
            groupedLocations={groupedLocations}
            search={multiLocationSearch}
            onSearchChange={setMultiLocationSearch}
            open={openSection === 'multilocation'}
            onToggle={toggleSection('multilocation')}
          />
          {timeFrequencySection}
          {variableSection}
          <ForecastDateSection
            forecastDate={forecastDate}
            open={openSection === 'forecastdate'}
            onToggle={toggleSection('forecastdate')}
          />
        </>
      )}

      {subpage === 'rounds' && (
        <>
          <GroupSection
            groupedLocations={groupedLocations}
            search={groupSearch}
            onSearchChange={setGroupSearch}
            open={openSection === 'group'}
            onToggle={toggleSection('group')}
          />
          <LocationSection
            groupedLocations={groupedLocations}
            search={locationSearch}
            onSearchChange={setLocationSearch}
            open={openSection === 'location'}
            onToggle={toggleSection('location')}
          />
          {timeFrequencySection}
          {variableSection}
          <RoundsSection
            rounds={rounds}
            open={openSection === 'rounds'}
            onToggle={toggleSection('rounds')}
          />
        </>
      )}
    </SheetFrame>
  );
}
